import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Bot, Zap, CheckCircle, ArrowRight, Database, Target, Shield, Clock, BarChart3, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const ZohoAIAgent = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 AI Agent for Zoho CRM",
      "applicationCategory": "BusinessApplication",
      "description": "AI agent that automates Zoho CRM — data entry, enrichment, pipeline management, and forecasting without manual work.",
      "url": "https://noq5.ai/zoho-ai-agent",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Does NoQ5 work with Zoho CRM?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. NoQ5 integrates with Zoho CRM to automate data entry, contact enrichment, deal tracking, and pipeline management. Setup takes minutes and works alongside Zoho's native features." } },
        { "@type": "Question", "name": "Is NoQ5 better than Zoho Zia?", "acceptedAnswer": { "@type": "Answer", "text": "Zoho Zia provides AI-powered suggestions and predictions within Zoho. NoQ5 goes further by autonomously executing CRM tasks — logging activities, enriching data, and updating pipelines without human intervention. NoQ5 acts, Zia suggests." } }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="Zoho CRM AI Agent — Automate Zoho with AI | NoQ5"
        description="Add AI agents to Zoho CRM. Automate data entry, enrichment, pipeline updates, and forecasting. NoQ5 makes Zoho run itself — no manual work required."
        keywords="Zoho AI agent, Zoho CRM automation, AI for Zoho CRM, Zoho CRM AI, automate Zoho CRM, Zoho Zia alternative, Zoho CRM data entry automation"
        canonicalUrl="https://noq5.ai/zoho-ai-agent"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">ZOHO CRM + AI</span>
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
            Make Zoho CRM<br /><span className="text-primary">Run Itself</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Zoho Zia gives you suggestions. NoQ5 gives you execution. Add an AI agent to your Zoho CRM that autonomously handles data entry, enrichment, and pipeline management.
          </p>
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
            <Link to="/trial">Connect Zoho CRM <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      {/* Zia vs NoQ5 */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Zoho Zia vs. NoQ5 AI Agent</h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-card rounded-xl border border-border">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-4 text-left font-bold">Capability</th>
                  <th className="p-4 text-center font-bold">Zoho Zia</th>
                  <th className="p-4 text-center font-bold text-primary">NoQ5</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Auto-log emails & calls", "Partial", "Full"],
                  ["Data enrichment", "Basic", "50+ sources"],
                  ["Pipeline forecasting", "Predictions", "Evidence-based"],
                  ["Deal stage automation", "Rules-based", "AI-autonomous"],
                  ["Contact deduplication", "Manual trigger", "Continuous"],
                  ["Meeting notes → CRM", "No", "Automatic"],
                  ["Works outside Zoho ecosystem", "No", "Yes"],
                  ["Setup time", "Complex", "5 minutes"]
                ].map(([feature, zia, noq5], i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="p-4 font-medium">{feature}</td>
                    <td className="p-4 text-center text-muted-foreground">{zia}</td>
                    <td className="p-4 text-center text-primary font-semibold">{noq5}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What NoQ5 Does for Zoho */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">What NoQ5 Automates in Zoho CRM</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Database, title: "Contact & Lead Enrichment", desc: "Every new lead in Zoho gets enriched with company data, job titles, tech stack, and social profiles from 50+ sources." },
              { icon: RefreshCw, title: "Automatic Activity Logging", desc: "Emails, calls, and meetings logged to the right records automatically — no manual entry needed." },
              { icon: BarChart3, title: "Pipeline Intelligence", desc: "Deal stages updated based on actual buyer engagement. Forecasts built on evidence, not hope." },
              { icon: Target, title: "Lead Scoring & Qualification", desc: "Leads scored using your methodology (BANT, MEDDIC, custom) based on real engagement signals." }
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border flex gap-4">
                <item.icon className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Supercharge Zoho CRM with AI</h2>
          <p className="text-xl opacity-90 mb-8">Connect NoQ5 to Zoho CRM in 5 minutes. No complex setup, no workflow builders, no consultants needed.</p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ZohoAIAgent;
